// filepath: apps/web/src/features/promote/RejectReasonDialog.tsx
//
// FR-UI-PROM-2 AC-4 — modal dialog that captures a free-text
// rejection reason before firing useRejectPromotion.
//
// INV-9 compliant: the textarea value is rendered via React's
// controlled-component pattern (the textarea owns its own DOM
// state) and is POSTed verbatim as the `reason` field on the
// rejection request. The server-side handler stores the value in
// the promotion_reviews row's metadata (FR-PROM-3). No
// dangerouslySetInnerHTML anywhere.
//
// Non-skippable: the only way out of this dialog is Cancel
// (returns to the gate page, no row transition) or Confirm
// Reject (fires the mutation). ESC keypress is handled by the
// dialog wrapper (calls onCancel). Click-outside closes via
// onCancel. There is NO "auto-reject" / "blank-reason" /
// "submit-later" path.

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { useRejectPromotion } from "./useRejectPromotion";

interface RejectReasonDialogProps {
  reviewId: string;
  open: boolean;
  onCancel: () => void;
  onRejected: () => void;
}

export function RejectReasonDialog({
  reviewId,
  open,
  onCancel,
  onRejected,
}: RejectReasonDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const rejectMutation = useRejectPromotion();
  const trimmed = reason.trim();
  const canSubmit = trimmed.length > 0 && !rejectMutation.isPending;

  if (!open) return null;

  async function handleConfirm() {
    if (!canSubmit) return;
    await rejectMutation.mutateAsync({ reviewId, reason: trimmed });
    setReason("");
    onRejected();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reject-reason-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="reject-reason-dialog"
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg">
        <h2 id="reject-reason-title" className="text-sm font-semibold">
          {t("promote.gate.reject.reason_label")}
        </h2>
        <label className="mt-2 block text-xs text-muted-foreground">
          {t("promote.gate.reject.reason_label")}
          <textarea
            className="mt-1 block w-full rounded border border-input bg-background px-2 py-1 text-sm"
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("promote.gate.reject.reason_placeholder")}
            disabled={rejectMutation.isPending}
            data-testid="reject-reason-textarea"
            aria-label={t("promote.gate.reject.reason_label")}
          />
        </label>
        {rejectMutation.isError && (
          <p
            className="mt-2 text-xs text-destructive"
            role="alert"
            data-testid="reject-reason-error"
          >
            {t("promote.gate.reject.error")}
          </p>
        )}
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={rejectMutation.isPending}
            className="rounded border border-border px-3 py-1 text-xs"
            data-testid="reject-reason-cancel"
          >
            {t("promote.gate.reject.cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="rounded bg-destructive px-3 py-1 text-xs text-destructive-foreground disabled:opacity-50"
            data-testid="reject-reason-confirm"
          >
            {rejectMutation.isPending
              ? t("promote.gate.reject.pending")
              : t("promote.gate.reject.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
